import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaClock, FaEye, FaPlus } from "react-icons/fa";
import styles from "./Listshift.module.css";
import { CustomButton } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { useSelector } from "react-redux";
const ShiftList = () => {
  const navigate = useNavigate();
  const { language, translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  /* ===========================
     FETCH SHIFT LIST API
     =========================== */
  useEffect(() => {
    if (school_id) fetchShiftList();
  }, [school_id]);

  const fetchShiftList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(
        `/school/shiftList/${school_id}`,
      );

      if (response?.data?.status) {
        const transformedData = response.data.data.map((shift) => ({
          id: shift.id, // keep internally for view/edit
          shift_name: shift.shift_type || "N/A",
          from_time: shift.shift_from_time || "N/A",
          to_time: shift.shift_to_time || "N/A",
          no_of_periods: shift.no_of_period || 0,
          no_of_breaks: shift.no_of_break || 0,
          status: true,
          originalData: shift,
        }));

        setData(transformedData);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching shift list", error);

      // fallback sample data
      setData([
        {
          id: 1,
          shift_name: "SHIFT 1",
          from_time: "09:30",
          to_time: "16:20",
          no_of_periods: 8,
          no_of_breaks: 2,
          status: true,
        },
        {
          id: 2,
          shift_name: "SHIFT 2",
          from_time: "08:00",
          to_time: "14:00",
          no_of_periods: 6,
          no_of_breaks: 1,
          status: false,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  /* ===========================
     SEARCH FILTER
     =========================== */
  const filteredData = useMemo(() => {
    if (!apiSearch) return data;

    const searchLower = apiSearch.toLowerCase();
    return data.filter(
      (item) =>
        item.shift_name?.toLowerCase().includes(searchLower) ||
        item.from_time?.includes(searchLower) ||
        item.to_time?.includes(searchLower) ||
        String(item.no_of_breaks)?.includes(searchLower),
    );
  }, [data, apiSearch]);

  /* ===========================
     PAGINATION
     =========================== */
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination]);

  /* ===========================
     HANDLERS
     =========================== */
  const handleAdd = () => {
    navigate("/user/ShiftCreate");
  };

  const handleView = (row) => {
    navigate("/user/ShiftCreate", {
      state: {
        id: row.id,
        schoolId: school_id,
      },
    });
  };

  /* ===========================
     TABLE COLUMNS
     =========================== */
  const columns = useMemo(
    () => [
      {
        accessorKey: "shift_name",
        header: translations.labels.shiftLabel,
        enableSorting: true,
      },
      {
        accessorKey: "from_time",
        header: translations.labels.fromTime,
        enableSorting: true,
      },
      {
        accessorKey: "to_time",
        header: translations.labels.toTime,
        enableSorting: true,
      },
      {
        accessorKey: "no_of_breaks",
        header: translations.labels.noOfBreak,
        enableSorting: true,
      },
      {
        accessorKey: "no_of_periods",
        header: translations.labels.noOfPeriods,
        enableSorting: true,
      },
      {
        id: "actions",
        header: translations.labels.actions,
        enableSorting: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title="View"
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <div className={styles.container}>
      {/* HEADER */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaClock />
        </div>
        <h1 className={styles.headerTitle}>{translations.labels.shiftConfigurationList}</h1>
      </div>

      {/* ACTIONS */}
      <div className={styles.actions}>
        <CustomButton
          type="button"
          variant="contained"
          onClick={handleAdd}
          fullWidth={false}
        >
           <FaPlus style={{ marginRight: "8px" }} />
          {translations.labels.add}
        </CustomButton>
      </div>

      {/* TABLE */}
      <CustomMRTTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        pagination={pagination}
        rowCount={filteredData.length}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        enableSearch={false}        // ❌ remove global search
        enableFiltering={false}
      />
    </div>
  );
};

export default ShiftList;
